// src/components/PreviewModal.jsx

import React, { useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import PreviewEnvelope from './GreetingCard/PreviewEnvelope'
import Button from './ui/Button'
import { CARD_W, CARD_H } from '../utils/geometry'

export default function PreviewModal({ cardState, onClose }) {
  const [opened, setOpened] = useState(false)
  const [flipped, setFlipped] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)

  const {
    imageUrl,
    videoUrl,       // NEW: Video URL for video greeting cards
    mediaType = "image", // NEW: "image" or "video"
    text,
    setText,
    inside,
    setInside,
    insideFont,
    insideFontSize,
    insideFill,
    insideBorderColor,
    insideBorderWidth,
    insideBorderStyle,
    coverBorderColor,
    coverBorderWidth,
    coverBorderStyle,
    coverOverlay,
    coverOverlayColor,
    signatures = [],
    insidePages = [inside],
    cardWidth = CARD_W,
    cardHeight = CARD_H,
    insideLayout, // Inside text positioning
    insideTextEntries = null, // Multiple text entries for collaboration
  } = cardState

  // Calculate scale factor to fit envelope in viewport while maintaining original proportions
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800
  const isMobile = viewportWidth < 768
  
  // Target display size - much larger on mobile to fill the modal better
  // Reserve space for modal padding, buttons, and some breathing room
  const availableWidth = viewportWidth - (isMobile ? 48 : 80) // Account for modal padding
  const availableHeight = viewportHeight - (isMobile ? 280 : 320) // Reserve space for buttons and header
  
  // On mobile, use more of the available space
  const targetWidth = isMobile ? availableWidth * 0.95 : Math.min(availableWidth * 0.7, 600)
  const targetHeight = isMobile ? availableHeight * 0.85 : Math.min(availableHeight * 0.8, 700)
  
  // Calculate scale to fit both dimensions while maintaining aspect ratio
  const scaleX = targetWidth / cardWidth
  const scaleY = targetHeight / cardHeight
  const scale = Math.min(scaleX, scaleY)

  const nextPage = () => {
    if (currentPage < insidePages.length - 1) {
      setCurrentPage(p => p + 1)
    }
  }

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(p => p - 1)
    } else {
      // If on first page, go back to cover
      setFlipped(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-[90vw] max-h-[90vh] overflow-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
        >
          <X size={24} />
        </button>

        {/* Envelope preview - extra padding for card animation */}
        <div className="flex flex-col items-center pt-8 pb-4 px-4">
          {/* Outer container reserves the scaled visual space in layout */}
          <div
            style={{
              width: cardWidth * scale,
              height: cardHeight * scale,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
            }}
          >
            {/* Inner wrapper with CSS transform for scaling - maintains original proportions */}
            <div
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top center',
                width: cardWidth,
                height: cardHeight,
              }}
            >
              <PreviewEnvelope
                opened={opened}
                flipped={flipped}
                imageUrl={imageUrl}
                videoUrl={videoUrl}
                mediaType={mediaType}
                text={{ ...text }}
                setText={setText}
                inside={insidePages[currentPage]}
                setInside={(newText) => {
                  const newPages = [...insidePages];
                  newPages[currentPage] = newText;
                  setInside(newPages.join('\n'));
                }}
                insideFont={insideFont || "'Inter', sans-serif"}
                insideFontSize={insideFontSize}
                insideFill={insideFill}
                insideBorderColor={insideBorderColor}
                insideBorderWidth={insideBorderWidth}
                insideBorderStyle={insideBorderStyle}
                coverBorderColor={coverBorderColor}
                coverBorderWidth={coverBorderWidth}
                coverBorderStyle={coverBorderStyle}
                coverOverlay={coverOverlay}
                coverOverlayColor={coverOverlayColor}
                signatures={signatures}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                insideLayout={insideLayout}
                insideTextEntries={insideTextEntries}
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <Button onClick={() => setOpened(o => !o)}>
              {opened ? 'Close Envelope' : 'Open Envelope'}
            </Button>
            <Button onClick={() => setFlipped(f => !f)} disabled={!opened}>
              {flipped ? 'Show Front' : 'Show Inside'}
            </Button>
          </div>

          {/* Page navigation */}
          {opened && flipped && insidePages.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <Button
                onClick={prevPage}
                variant="secondary"
                className="flex items-center gap-2"
              >
                <ChevronLeft size={16} /> 
                <span>Previous</span>
              </Button>
              <span className="text-sm text-gray-600 min-w-[80px] text-center">
                Page {currentPage + 1} of {insidePages.length}
              </span>
              <Button
                onClick={nextPage}
                disabled={currentPage === insidePages.length - 1}
                variant="secondary"
                className="flex items-center gap-2"
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
